import LesserEval from 'piivo-poster-engine/src/lib/util/lesserEval';
import Vue from 'vue';

import { TemplateForm } from '../../poster/api/types';
import { AttributeTypes } from '../constants';

/**
 * Iterates on the attributes of the group
 *
 * @param groups - groups of attributes
 * @param attributeCb - the attribute callback
 */
export function iterateAttrGroup(
  groups: TemplateForm.AttributeGroup[] | null,
  attributeCb: (attr: TemplateForm.Attribute) => void
): void {
  groups?.forEach(group => {
    group?.attributes?.forEach(attribute => {
      if (attribute) {
        attributeCb(attribute);
      }
    });
  });
}

/**
 * Iterate a form and launch callback on attributes.
 * @param form - Form to iterate
 * @param attributeCb - Callback function with attribute parameter
 * @param isInput - if the attribute is from an input group
 */
export function iterateForm(
  form: TemplateForm.Form,
  attributeCb: (attr: TemplateForm.Attribute, isInput: boolean) => void
): void {
  // Iterate on form input groups
  iterateAttrGroup(form.inputGroups, attr => attributeCb(attr, true));

  // Iterate on form attribute groups
  iterateAttrGroup(form.attributeGroups, attr => attributeCb(attr, false));
}

/**
 * Manage update on current attribute alias (attribute change).
 * @param form - Form object with attribute groups
 * @param attribute - the attribute for whom to update *for*
 * @param actionCb - Function to execute on the attributes that react to the current attribute change (take an attribute alias in parameter)
 * @param managedAliases - Array of attribute aliases already managed (prevent infinite loop)
 */
export function updateOn(
  form: TemplateForm.Form,
  attribute: TemplateForm.Attribute,
  actionCb: (attr: TemplateForm.Attribute) => void,
  managedAliases: string[] = []
): void {
  if (managedAliases.includes(attribute.alias)) {
    return;
  }

  managedAliases.push(attribute.alias);
  if (!form) {
    return;
  }

  iterateForm(form, iterateAttr => {
    // Action on attribute that update on current alias and not already managed (+ call recursively)
    if (
      iterateAttr.updateOn != null &&
      iterateAttr.updateOn.length > 0 &&
      iterateAttr.updateOn.includes(attribute.alias) &&
      !managedAliases.includes(iterateAttr.alias)
    ) {
      // Launch callback for attribute alias found
      actionCb(iterateAttr);

      // Call updateOn for current attribute change
      updateOn(form, iterateAttr, actionCb, managedAliases);
    }
  });
}

/**
 * Iterates the column attributes of the table. Invokes `actionCb` for the attributes
 * whose `updateOn` includes the alias of `currentAttr`.
 * Also launches `updateOnTable` on the found attributes
 *
 * @param tableAttr - the table attribute
 * @param actionCb - Callback that receives the attribute and the column index that should be updated
 * @param currentAttr - the attribute for whom to update *for*
 * @param managedIds - Array of attribute itemIds for whom NOT to invoke the callback
 */
export function updateOnTable(
  tableAttr: TemplateForm.Attribute,
  actionCb: (colAttr: TemplateForm.Attribute, colIndex: number) => void,
  currentAttr: TemplateForm.Attribute,
  managedIds: string[] = []
): void {
  tableAttr.options?.displayOptions?.tableOptions?.columns?.forEach((column, colIndex) => {
    const iterateAttr = column.attribute;
    if (!iterateAttr) {
      return;
    }
    // Action on attribute that update on current itemId and not already managed (+ call recursively)
    if (
      iterateAttr.updateOn != null &&
      iterateAttr.updateOn.length > 0 &&
      iterateAttr.updateOn.includes(currentAttr.alias) &&
      !managedIds.includes(iterateAttr.itemId)
    ) {
      // Launch callback for attribute alias found
      actionCb(iterateAttr, colIndex);
      managedIds.push(iterateAttr.itemId);

      // Call updateOnTable for current attribute change
      updateOnTable(tableAttr, actionCb, currentAttr, managedIds);
    }
  });
}

/**
 * Get default value from the attribute parameter
 * @param attribute - Attribute
 * @returns the attribute default value
 */
export function getAttributeDefaultValue(attribute: TemplateForm.Attribute): unknown {
  if (attribute == null || attribute.defaultValue == null) {
    return null;
  }

  let defaultValue = attribute.defaultValue;

  // TODO: evaluate 'defaultValue' with new eval when implemented
  if (typeof defaultValue === 'string') {
    // For now, extract the expression from "$X{expr}" and evaluate it
    const res = /^\$X\{(.+)\}$/.exec(defaultValue.trim());
    if (res && res[1]) {
      defaultValue = LesserEval.evalExpression(res[1], {}, {});
    }
  }

  switch (attribute.type) {
    // At this point, defaultValue should be a string
    case AttributeTypes.DATE: {
      if (attribute.options && attribute.options.showTime) {
        defaultValue = Vue.filter('isoDateFull')(defaultValue);
      } else {
        defaultValue = Vue.filter('isoDate')(defaultValue);
      }
      break;
    }
    case AttributeTypes.LINKS: {
      // At this point, defaultValue should be a string (alias) or array (of aliases or option objects)
      if (defaultValue) {
        // Ensure value is an array
        defaultValue = Array.isArray(defaultValue) ? defaultValue : [defaultValue];

        // Ensure the correct amount of options are selected
        if (defaultValue.length) {
          defaultValue =
            attribute.options && attribute.options.multiSelect === true
              ? defaultValue
              : [defaultValue[0]];
        }
      } else {
        defaultValue = [];
      }
      break;
    }
    case AttributeTypes.STRING: {
      // At this point, defaultValue should be a string
      if (attribute.options.multiple) {
        defaultValue = defaultValue ? JSON.parse(defaultValue) : [];
      }
      break;
    }
  }

  return defaultValue;
}

/**
 * @param value - value to convert
 * @returns number or `NaN` if couldn't be converted
 */
export function parseNumericValue(value: unknown): number {
  return Number(value);
}

/**
 * Return a normalized value for a form attribute
 *
 * TODO: Refactor for all types and transform that to a static class.
 * @param alias
 * @param value
 * @param attribute - the attribute of the alias
 * @param fallbackType - the type of the attribute alias to use if attribute is unknown
 * @returns Normalized value
 */
export function getNormalizedValue(
  value: any,
  attribute: TemplateForm.Attribute | null,
  fallbackType: AttributeTypes | null
): any {
  const normalizers: { [x: string]: (value: any, attr: TemplateForm.Attribute | null) => any } = {
    Date: (value, attr) => {
      if (attr && attr.options.showTime) {
        return Vue.filter('isoDateFull')(value);
      }
      return Vue.filter('isoDate')(value);
    },
  };

  // Get alias type
  const myType = attribute?.type ?? fallbackType ?? null;
  const myAttribute = attribute ?? null;

  // Normalize value
  if (myType) {
    const normalizer = normalizers[myType];
    if (normalizer && value) {
      return normalizer(value, myAttribute);
    }
  }
  return value;
}
